import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  GeoPoint
} from 'firebase/firestore';
import { db } from '../firebase/config';

const INITIAL_BATCH_SIZE = 50;
const VIEWPORT_BATCH_SIZE = 30;
const ZOOM_LEVEL_THRESHOLD = 8;

export function useMapData() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [center, setCenter] = useState({ lat: 51.509865, lng: -0.118092 });
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(5);
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [filters, setFilters] = useState({
    materialType: '',
    features: [],
    era: '',
    countries: []
  });
  
  const loadedSiteIds = useRef(new Set());
  const pendingViewportUpdate = useRef(false);
  const spatialQueryCache = useRef(new Map());

  // Generate cache key for spatial queries
  const getCacheKey = useCallback((bounds, zoom) => {
    if (!bounds) return null;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    return `${ne.lat()}|${ne.lng()}|${sw.lat()}|${sw.lng()}|${zoom}`;
  }, []);

  // Process Firestore document with optimized data structure
  const processSiteDoc = useCallback((doc) => {
    const data = doc.data();
    loadedSiteIds.current.add(doc.id);
    
    const coords = data.location?.coordinates;
    const siteLat = coords?.latitude ?? coords?.lat ?? coords?._lat;
    const siteLng = coords?.longitude ?? coords?.lng ?? coords?._long;

    return {
      id: doc.id,
      name: data.name,
      location: {
        ...data.location,
        coordinates: (typeof siteLat === 'number' && typeof siteLng === 'number') ? 
          { lat: siteLat, lng: siteLng } : null
      },
      media: Array.isArray(data.mapPreviewMedia) ? data.mapPreviewMedia : [],
      features_normalized: data.features_normalized || [],
      mapPriority: data.mapPriority || 0,
      country: data.location?.country || ''
    };
  }, []);

  // Initial data load - fetch high-priority sites
  useEffect(() => {
    const fetchInitialSites = async () => {
      try {
        setLoading(true);
        const q = query(
          collection(db, 'sitesearch_beta'),
          orderBy('mapPriority', 'desc'),
          limit(INITIAL_BATCH_SIZE)
        );
        
        const snapshot = await getDocs(q);
        if (snapshot.empty) return;
        
        const validSites = snapshot.docs.map(processSiteDoc).filter(site => 
          site.location?.coordinates && 
          (site.location.coordinates.lat !== undefined || site.location.coordinates.latitude !== undefined) &&
          (site.location.coordinates.lng !== undefined || site.location.coordinates.longitude !== undefined)
        );
        
        setSites(validSites);
      } catch (error) {
        console.error('Error fetching initial sites:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialSites();
  }, [processSiteDoc]);

  // Update viewport with spatial query optimization
  const updateMapViewport = useCallback((newBounds, newZoom) => {
    setBounds(newBounds);
    setZoom(newZoom);

    if (newZoom < ZOOM_LEVEL_THRESHOLD || !newBounds) return;
    
    // Use cache when possible
    const cacheKey = getCacheKey(newBounds, newZoom);
    if (cacheKey && spatialQueryCache.current.has(cacheKey)) {
      const cachedSites = spatialQueryCache.current.get(cacheKey);
      setSites(cachedSites);
      return;
    }

    // Debounce spatial queries
    if (pendingViewportUpdate.current) return;
    pendingViewportUpdate.current = true;
    
    setTimeout(() => {
      loadSitesInViewport(newBounds, newZoom);
      pendingViewportUpdate.current = false;
    }, 300);
  }, [getCacheKey]);

  // Load sites within viewport bounds
  const loadSitesInViewport = useCallback(async (viewportBounds, zoomLevel) => {
    if (!viewportBounds) return;
    
    try {
      const ne = viewportBounds.getNorthEast();
      const sw = viewportBounds.getSouthWest();
      
      const q = query(
        collection(db, 'sitesearch_beta'),
        where('location.coordinates', '>=', new GeoPoint(sw.lat(), sw.lng())),
        where('location.coordinates', '<=', new GeoPoint(ne.lat(), ne.lng())),
        orderBy('mapPriority', 'desc'),
        limit(VIEWPORT_BATCH_SIZE)
      );
      
      const snapshot = await getDocs(q);
      const sites = snapshot.docs.map(processSiteDoc);
      
      // Cache results
      const cacheKey = getCacheKey(viewportBounds, zoomLevel);
      if (cacheKey) {
        spatialQueryCache.current.set(cacheKey, sites);
      }
      
      // Merge with existing sites
      setSites(prev => {
        const siteMap = new Map(prev.map(s => [s.id, s]));
        sites.forEach(s => siteMap.set(s.id, s));
        return Array.from(siteMap.values());
      });
    } catch (error) {
      console.error('Error loading sites in viewport:', error);
    }
  }, [processSiteDoc, getCacheKey]);

  return {
    sites,
    loading,
    center,
    setCenter,
    bounds,
    zoom,
    filters,
    setFilters,
    updateMapViewport,
    activeMarkerId,
    setActiveMarkerId
  };
}
